import React from 'react';
import { connect } from 'react-redux';
import { graphql, compose, gql } from 'react-apollo';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { injectIntl } from 'react-intl';
import { Search, Sparkles, Anchor, Ship, MapPin, Hand } from 'lucide-react';
import cx from 'classnames';

import { setStickyHeader } from '../../../actions/stickyHeader';
import { getActiveCategory } from '../../../reducers/category';

import history from '../../../core/history';
import { trackSearch } from '../../../helpers/pixel';
import s from './AIBanner.css';
import CategorySelector from '../../CategorySelector';

// Default placeholder phrases that rotate
const DEFAULT_PLACEHOLDER_PHRASES = [
  'Yate en Miami para 10 personas...',
  'Velero romantico en Cap Cana...',
  'Pesca deportiva en Culebra...',
  'Fiesta en yate para 20 invitados...',
  'Paseo familiar en lancha...'
];

// Default suggestions (fallback if no popular searches)
const DEFAULT_SUGGESTIONS = [
  { label: 'Yates en Miami', query: 'yate miami' },
  { label: 'Veleros romanticos', query: 'velero romantico' },
  { label: 'Pesca deportiva', query: 'pesca' },
];

// Keywords database for intelligent parsing
const LOCATION_KEYWORDS = {
  'miami': 'Miami, FL, USA',
  'cap cana': 'Cap Cana, Dominican Republic',
  'culebra': 'Culebra, Puerto Rico',
  'punta cana': 'Punta Cana, Dominican Republic',
  'cancun': 'Cancun, Mexico',
  'bahamas': 'Nassau, Bahamas',
  'cartagena': 'Cartagena, Colombia',
  'san juan': 'San Juan, Puerto Rico'
};

const BOAT_TYPE_KEYWORDS = {
  'yate': 'yacht',
  'yacht': 'yacht',
  'velero': 'sailboat',
  'sailboat': 'sailboat',
  'lancha': 'motorboat',
  'catamaran': 'catamaran',
  'catamarán': 'catamaran',
  'bote': 'boat',
  'boat': 'boat'
};

const CAPACITY_PATTERNS = [
  /(\d+)\s*personas?/i,
  /(\d+)\s*invitados?/i,
  /(\d+)\s*guests?/i,
  /para\s*(\d+)/i,
  /for\s*(\d+)/i
];

// Module-level flag to prevent animation restart on re-renders
let hasAnimatedOnce = false;

class AIBanner extends React.Component {
  constructor(props) {
    super(props);
    // Only animate on first mount (not on re-renders from GraphQL/auth)
    this.shouldAnimate = !hasAnimatedOnce;
    hasAnimatedOnce = true;
    this.state = {
      searchQuery: '',
      currentPlaceholderIndex: 0,
      isInputFocused: false,
      isScrolled: false,
      isMounted: false // CSS animation trigger after hydration
    };
    this.placeholderInterval = null;
    this.handleScroll = this.handleScroll.bind(this);
  }

  // FIX: Eliminado componentWillUnmount duplicado (el segundo en linea ~194 es el que se ejecutaba)
  // handleAnimationComplete ya no necesita marcar hasAnimatedOnce (se marca en el constructor)

  componentDidMount() {
    // Trigger CSS animations after background has time to render
    if (this.shouldAnimate) {
      // Wait 500ms so background image loads first, THEN animate text in
      this.animTimeout = setTimeout(() => {
        this.setState({ isMounted: true });
      }, 500);
    } else {
      this.setState({ isMounted: true });
    }

    // Rotate placeholder text
    this.placeholderInterval = setInterval(() => {
      const phrases = this.getPlaceholderPhrases();
      this.setState(prevState => ({
        currentPlaceholderIndex: (prevState.currentPlaceholderIndex + 1) % phrases.length
      }));
    }, 4000);

    // Scroll listener for sticky search bar
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }
  }

  getPlaceholderPhrases = () => {
    // Always use fixed placeholder phrases
    return DEFAULT_PLACEHOLDER_PHRASES;
  }

  getSuggestions = () => {
    const { popularSearches } = this.props;
    if (popularSearches?.getPopularSearches?.length > 0) {
      return popularSearches.getPopularSearches.slice(0, 3).map(s => ({
        label: this.capitalizeWords(s.searchQuery),
        query: s.searchQuery
      }));
    }
    return DEFAULT_SUGGESTIONS;
  }

  capitalizeWords = (str) => {
    return str.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  componentWillUnmount() {
    if (this.placeholderInterval) {
      clearInterval(this.placeholderInterval);
    }
    if (this.animTimeout) {
      clearTimeout(this.animTimeout);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll);
    }
    const { setStickyHeader } = this.props;
    if (setStickyHeader) {
      setStickyHeader(false);
    }
    hasAnimatedOnce = false;
  }

  handleScroll() {
    // SYNC: Threshold 400px - sincronizado con HomeHeader.js
    const scrollThreshold = 400;
    const currentScrollY = window.scrollY;
    const { isScrolled } = this.state;
    const { setStickyHeader } = this.props;

    if (currentScrollY > scrollThreshold && !isScrolled) {
      this.setState({ isScrolled: true });
      if (setStickyHeader) setStickyHeader(true);
    } else if (currentScrollY <= scrollThreshold && isScrolled) {
      this.setState({ isScrolled: false });
      if (setStickyHeader) setStickyHeader(false);
    }
  }

  parseSearchQuery = (query) => {
    const lowerQuery = query.toLowerCase();
    const result = {
      location: null,
      boatType: null,
      capacity: null,
      rawQuery: query
    };

    // Extract location
    for (const [keyword, fullLocation] of Object.entries(LOCATION_KEYWORDS)) {
      if (lowerQuery.includes(keyword)) {
        result.location = fullLocation;
        break;
      }
    }

    // Extract boat type
    for (const [keyword, type] of Object.entries(BOAT_TYPE_KEYWORDS)) {
      if (lowerQuery.includes(keyword)) {
        result.boatType = type;
        break;
      }
    }

    // Extract capacity
    for (const pattern of CAPACITY_PATTERNS) {
      const match = lowerQuery.match(pattern);
      if (match) {
        result.capacity = parseInt(match[1], 10);
        break;
      }
    }

    return result;
  };

  buildSearchUrl = (parsed) => {
    const { activeCategory } = this.props;
    const params = new URLSearchParams();

    if (parsed.location) {
      params.set('address', parsed.location);
      params.set('chosen', '1');
    }

    if (parsed.capacity) {
      params.set('personCapacity', parsed.capacity.toString());
    }

    // If no specific location found, use the raw query as geography
    if (!parsed.location && parsed.rawQuery.trim()) {
      params.set('address', parsed.rawQuery.trim());
      params.set('chosen', '1');
    }

    // Add categoryType if not boat
    if (activeCategory && activeCategory !== 'boat') {
      params.set('categoryType', activeCategory);
    }

    return `/s?${params.toString()}`;
  };

  handleSearch = (e) => {
    e && e.preventDefault();
    const { searchQuery } = this.state;
    const { trackSearchMutation } = this.props;

    if (!searchQuery.trim()) return;

    // Track the search query
    if (trackSearchMutation) {
      trackSearchMutation({
        variables: { searchQuery: searchQuery.trim() }
      }).catch(err => console.log('Error tracking search:', err));
    }

    const parsed = this.parseSearchQuery(searchQuery);
    const searchUrl = this.buildSearchUrl(parsed);

    trackSearch({ searchQuery: searchQuery.trim(), location: parsed.location });

    history.push(searchUrl);
  };

  handleSuggestionClick = (query) => {
    this.setState({ searchQuery: query }, () => {
      this.handleSearch();
    });
  };

  handleInputChange = (e) => {
    this.setState({ searchQuery: e.target.value });
  };

  handleInputFocus = () => {
    this.setState({ isInputFocused: true });
  };

  handleInputBlur = () => {
    this.setState({ isInputFocused: false });
  };

  handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.handleSearch(e);
    }
  };

  render() {
    const { title, content, bannerImage, intl } = this.props;
    const { searchQuery, currentPlaceholderIndex, isInputFocused, isScrolled, isMounted } = this.state;

    const phrases = this.getPlaceholderPhrases();
    const suggestions = this.getSuggestions();
    const currentPlaceholder = phrases[currentPlaceholderIndex % phrases.length];

    const shouldAnimate = this.shouldAnimate;

    // Build class for animation state
    const contentClass = cx(
      s.heroContent,
      shouldAnimate && s.heroAnimatable,
      isMounted && s.heroVisible
    );

    return (
      <section className={s.heroSection}>
        {/* Full-screen background */}
        <div
          className={s.heroBackground}
          style={{ backgroundImage: bannerImage ? `url(${bannerImage})` : 'linear-gradient(135deg, #0a1628 0%, #1a365d 100%)' }}
        />

        {/* Gradient overlay */}
        <div className={s.heroOverlay} />

        {/* Floating decorative elements - pure CSS */}
        <div className={s.floatingElements}>
          <div className={s.floatIcon1}>
            <Anchor size={24} />
          </div>
          <div className={s.floatIcon2}>
            <Ship size={28} />
          </div>
        </div>

        {/* Main content */}
        <div className={contentClass}>
          {/* Category Selector */}
          <div className={s.heroAnimItem} style={{ '--anim-order': 0 }}>
            <CategorySelector />
          </div>

          {/* Line 1: White */}
          <p className={cx(s.heroLine1, s.heroAnimItem)} style={{ '--anim-order': 1 }}>
            {content}
          </p>

          {/* Line 2: Gradient */}
          <h1 className={cx(s.heroLine2, s.heroAnimItem)} style={{ '--anim-order': 2 }}>
            {title}
          </h1>

          {/* Search bar - hides when scrolled */}
          <div
            className={cx(
              s.heroSearchContainer,
              s.heroAnimItem,
              isScrolled && s.heroSearchHidden
            )}
            style={{ '--anim-order': 3 }}
          >
            <form onSubmit={this.handleSearch} className={s.heroSearchForm}>
              <div className={s.heroSearchWrapper}>
                <div className={s.searchIconWrapper}>
                  <MapPin size={20} className={s.searchMapIcon} />
                </div>
                <input
                  type="text"
                  className={s.heroSearchInput}
                  value={searchQuery}
                  onChange={this.handleInputChange}
                  onFocus={this.handleInputFocus}
                  onBlur={this.handleInputBlur}
                  onKeyPress={this.handleKeyPress}
                  placeholder={!isInputFocused ? currentPlaceholder : 'Escribe tu búsqueda...'}
                  aria-label="Buscar botes"
                />
                <button
                  type="submit"
                  className={s.heroSearchButton}
                >
                  <Sparkles size={18} className={s.buttonSparkle} />
                  <span className={s.buttonText}>Buscar con IA</span>
                  <Search size={18} className={s.buttonSearchIcon} />
                </button>
              </div>
            </form>
          </div>

          {/* Quick filter chips */}
          <div className={cx(s.heroChipsContainer, isScrolled && s.heroChipsHidden)}>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className={cx(s.heroChip, s.heroAnimItem)}
                onClick={() => this.handleSuggestionClick(suggestion.query)}
                type="button"
                style={{ '--anim-order': 4 + index }}
              >
                {suggestion.label}
              </button>
            ))}
          </div>

          {/* Trust indicators */}
          <div
            className={cx(s.trustIndicators, s.heroAnimItem)}
            style={{ '--anim-order': 7 }}
          >
            <div className={s.trustItem}>
              <span className={s.trustNumber}>10,000+</span>
              <span className={s.trustLabel}>Botes disponibles</span>
            </div>
            <div className={s.trustDivider} />
            <div className={s.trustItem}>
              <span className={s.trustNumber}>50+</span>
              <span className={s.trustLabel}>Destinos</span>
            </div>
            <div className={s.trustDivider} />
            <div className={s.trustItem}>
              <span className={s.trustNumber}>4.9★</span>
              <span className={s.trustLabel}>Rating promedio</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className={cx(s.scrollIndicator, s.heroAnimItem)}
          style={{ '--anim-order': 8 }}
        >
          <div className={s.scrollBounce}>
            <div className={cx(s.scrollMouse, s.desktopOnly)}>
              <div className={s.scrollWheel} />
            </div>
            <div className={cx(s.scrollHand, s.mobileOnly)}>
              <Hand size={28} strokeWidth={1.5} />
            </div>
          </div>
          <span className={s.scrollText}>Explorar</span>
        </div>
      </section>
    );
  }
}

const mapState = (state) => ({
  locale: state.intl.locale,
  isSticky: state.stickyHeader?.isSticky,
  activeCategory: getActiveCategory(state)
});

const mapDispatch = {
  setStickyHeader
};

// GraphQL queries
const GET_POPULAR_SEARCHES = gql`
  query getPopularSearches {
    getPopularSearches(limit: 5) {
      id
      searchQuery
      searchCount
    }
  }
`;

const TRACK_SEARCH = gql`
  mutation trackSearch($searchQuery: String!) {
    trackSearch(searchQuery: $searchQuery)
  }
`;

export default compose(
  injectIntl,
  connect(mapState, mapDispatch),
  withStyles(s),
  graphql(GET_POPULAR_SEARCHES, {
    name: 'popularSearches',
    options: {
      fetchPolicy: 'cache-and-network',
      ssr: true
    }
  }),
  graphql(TRACK_SEARCH, {
    name: 'trackSearchMutation'
  })
)(AIBanner);
